import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { ButtonWrapper, SearchInputWrapper, StyledButton } from './styled';
import DropdownSelect from '../../components/DropdownSelect';
import { academicYears } from '../../constants/academicYears';
import { SnackbarSeverityType } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import CancelledPaymentsLogTable from './components/CancelledPaymentsLogTable';
import { transformCancelledPaymentsLogData } from './utils/transformCancelledPaymentsLogData';
import { CancelledPaymentsLogProps } from './types';
import { useSchoolClassesConfigHook } from '../common/hooks/useSchoolClassesConfig';
import { isEmpty } from 'lodash';
import { downloadReport } from '../../utils/reportUtils';
import { PageWrapper, Title } from '../common/styled';
import fetchCancelledPaymentsLog from './utils/CancelledPaymentsLogPageUtils';

const CancelledPaymentsLogPage = () => {
  const [academicYear, setAcademicYear] = useState<string>('All');
  const [hasSearched, setHasSearched] = useState(false);
  const [grade, setGrade] = useState<string>('All');
  const [isSearching, setIsSearching] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [cancelledPaymentsLogData, setcancelledPaymentsLogData] = useState<
    CancelledPaymentsLogProps[]
  >([]);

  const { gradeOptions } = useSchoolClassesConfigHook();
  const gradeDropDownOptions = [
    { value: 'All', label: 'All' },
    ...gradeOptions
  ];

  const academicYearOptions = [
    { value: 'All', label: 'All' },
    ...academicYears
  ];

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleSearch = async () => {
    setIsSearching(true);
    try {
      const cancelledPaymentsLog = await fetchCancelledPaymentsLog(
        academicYear,
        grade
      );
      const transformedData =
        transformCancelledPaymentsLogData(cancelledPaymentsLog);
      setcancelledPaymentsLogData(transformedData);
      showToast(
        'Fetched cancelled payments log successfully',
        SnackbarSeverityType.success
      );
    } catch (error) {
      showToast(
        'Error in fetching cancelled payments log',
        SnackbarSeverityType.error
      );
    } finally {
      setIsSearching(false);
      setHasSearched(true);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const cancelledPaymentsLog = await fetchCancelledPaymentsLog(
        academicYear,
        grade
      );
      const transformedCancelledPaymentsLogData =
        transformCancelledPaymentsLogData(cancelledPaymentsLog);
      if (isEmpty(transformedCancelledPaymentsLogData)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }

      downloadReport(
        transformedCancelledPaymentsLogData,
        'Cancelled_Payments_Log_Data',
        'Cancelled Payments Log Data',
        showToast
      );
    } catch (error: any) {
      showToast(
        error.errors?.[0].message ||
          'Error downloading cancelled payments log report',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  return (
    <>
      <Title>Cancelled Payments Log</Title>
      <PageWrapper sx={{ minHeight: 'auto' }}>
        <SearchInputWrapper>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            name='academicYear'
            options={academicYearOptions}
            value={academicYear}
            onChange={(event) => {
              setAcademicYear(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />

          <DropdownSelect
            name='grade'
            label='Grade'
            placeholder='Select Grade'
            options={gradeDropDownOptions}
            value={grade}
            onChange={(event) => {
              setGrade(event.target.value as string);
              setHasSearched(false);
            }}
            hasTopLabel
          />

          <ButtonWrapper>
            <StyledButton onClick={handleSearch} disabled={isSearching}>
              {isSearching ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'View Report'
              )}
            </StyledButton>
            <StyledButton onClick={handleDownload} disabled={isSearching}>
              {isDownloading ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Download Report'
              )}
            </StyledButton>
          </ButtonWrapper>
        </SearchInputWrapper>
      </PageWrapper>
      {hasSearched && (
        <CancelledPaymentsLogTable
          cancelledPaymentsLogData={cancelledPaymentsLogData}
        />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default CancelledPaymentsLogPage;
